import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

//project data
const projects = [
  {
    id: "ezrah-avenue",
    title: "Ezrah",
    type: "Chalets",
    location: "Ashongman, Food Yard ",
    status: "Work In Progress",
    priceGHS: 5500000,
    priceUSD: 450000,
    image: "/ezrahavenue.jpg",
  },
  {
    id: "luxe-heights",
    title: "Luxe Heights Estates",
    type: "Estates",
    location: "Taifa, Accra",
    status: "Work In Progress",
    priceGHS: 7800000,
    priceUSD: 550000,
    image: "/luxeheights.jpg",
  },
  {
    id: "the-haven",
    title: "The Haven",
    type: "House",
    location: "Ashongman, Food Yard",
    status: "Coming Soon",
    priceGHS: 4200000,
    priceUSD: 330000,
    image: "/thehavern.jpg",
  },
];

// Filter options
const propertyTypes = ["All", "Apartment", "Chalets", "Penthouses", "Estates"];
const listingTypes = ["All", "Rent", "Sale"];
const currencies = ["GHS", "USD"];

// Utility for colored status badges
function StatusBadge({ status }) {
  const styles = {
    Completed: "bg-green-600 text-white",
    "Work In Progress": "bg-yellow-500 text-white",
    "Coming Soon": "bg-red-600 text-white",
  };
  return (
    <span
      className={`text-xs font-semibold px-2 py-1 rounded-full shadow ${styles[status]}`}
    >
      {status}
    </span>
  );
}

export default function ProjectsPage() {
  const [query, setQuery] = useState("");
  const [propertyType, setPropertyType] = useState("All");
  const [listingType, setListingType] = useState("All");
  const [currency, setCurrency] = useState("GHS");

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (propertyType !== "All" && p.type !== propertyType) return false;
      if (query && !p.title.toLowerCase().includes(query.toLowerCase()))
        return false;
      return true;
    });
  }, [query, propertyType]);

  const statusCounts = filteredProjects.reduce(
    (acc, p) => {
      acc[p.status] = (acc[p.status] || 0) + 1;
      return acc;
    },
    { Completed: 0, "Work In Progress": 0, "Coming Soon": 0 }
  );

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <section
        className="relative h-[60vh] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: "url('/luxeheights.jpg')" }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-6"
        >
          <div className="h-full flex flex-col justify-center items-center text-white">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
              Our luxurious touchpoints across the region
            </h1>
            <div className="space-x-4">
              <Link to="/how-to-buy">
                <button className="bg-indigo-600 px-6 py-2 rounded-md">
                  How to buy or rent
                </button>
              </Link>
              <a
                href="/brochure.pdf"
                download
                className="border border-white px-6 py-2 rounded-md"
              >
                Download our brochure
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-4 gap-8 px-6 py-12">
        {/* Filters + Projects */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-md flex flex-wrap gap-4">
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 px-4 py-2 border rounded"
            />
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className="px-4 py-2 border rounded"
            >
              {propertyTypes.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <select
              value={listingType}
              onChange={(e) => setListingType(e.target.value)}
              className="px-4 py-2 border rounded"
            >
              {listingTypes.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="px-4 py-2 border rounded"
            >
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {filteredProjects.map((p) => (
              <Link key={p.id} to={`/${p.id}`}>
                <motion.div
                  className="bg-white rounded-lg overflow-hidden shadow-md cursor-pointer"
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative h-48">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute top-3 left-3">
                      <StatusBadge status={p.status} />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg">{p.title}</h3>
                    <p className="text-sm text-gray-500">
                      {p.location} · {p.type}
                    </p>
                    <div className="mt-3 font-bold">
                      {currency === "GHS"
                        ? `GHS ${p.priceGHS.toLocaleString()}`
                        : `USD ${p.priceUSD.toLocaleString()}`}
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1 sticky top-28 space-y-6">
          {/* Project Statuses */}
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h4 className="font-semibold mb-4">Project Statuses</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg">
                <span className="flex items-center gap-2 text-green-700 font-medium">
                   Completed
                </span>
                <span className="bg-green-600 text-white text-sm font-semibold px-2 py-1 rounded-full shadow">
                  {statusCounts["Completed"] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between bg-yellow-50 px-3 py-2 rounded-lg">
                <span className="flex items-center gap-2 text-yellow-700 font-medium">
                   Work In Progress
                </span>
                <span className="bg-yellow-500 text-white text-sm font-semibold px-2 py-1 rounded-full shadow">
                  {statusCounts["Work In Progress"] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between bg-red-50 px-3 py-2 rounded-lg">
                <span className="flex items-center gap-2 text-red-700 font-medium">
                   Coming Soon
                </span>
                <span className="bg-red-600 text-white text-sm font-semibold px-2 py-1 rounded-full shadow">
                  {statusCounts["Coming Soon"] || 0}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
